import React, { useRef } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { SortOrder } from 'antd/lib/table/interface';
import InfiniteLoader from 'react-window-infinite-loader';

import { useStore } from 'zustand';
import {
  DEFAULT_USER_OVERVIEW_SORTING,
  userOverviewService,
  userOverviewStore,
  type OrderDirection,
  type UserOverviewDTO,
} from 'entities/userOverview';

import { withSuspense } from 'shared/lib/react';
import { Loader } from 'shared/ui/Loader';
import { VirtualizedTable } from 'shared/ui/VirtualTable';

import { columns, PAGE_SIZE, ROW_HEIGHT, THRESHOLD_VALUE } from './UserTable.const';
import { mapDataToTableColumns } from './UserTable.libs';

const UserTable: React.FC = () => {
  const loaderRef = useRef<InfiniteLoader>(null);

  const { filters, searchQuery, setOrder } = useStore(userOverviewStore);

  const {
    data: usersData,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery(userOverviewService.queryOptions({ filtration: filters, size: PAGE_SIZE, searchQuery }));

  const parseSortOrder = (sortOrder: SortOrder): OrderDirection => {
    return sortOrder === 'ascend' ? 'ASC' : 'DESC';
  };

  const handleSortingChange = (fieldName?: React.Key | readonly React.Key[], order?: SortOrder): void => {
    if (!fieldName || !order) {
      setOrder(DEFAULT_USER_OVERVIEW_SORTING);
      return;
    }
    const parsedOrder = parseSortOrder(order);
    const orders = [{ fieldName: fieldName as string, orderDirection: parsedOrder }];
    if (fieldName === 'firstName') {
      orders.push({ fieldName: 'lastName', orderDirection: parsedOrder });
    }
    setOrder(orders);
  };

  const tableData = usersData.pages.reduce<UserOverviewDTO[]>((acc, page) => [...acc, ...page.content], []);

  const tableDataMapped = mapDataToTableColumns(tableData);

  return (
    <VirtualizedTable
      columns={columns}
      dataSource={tableDataMapped}
      rowHeight={ROW_HEIGHT}
      loadNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isNextPageLoading={isFetching}
      onSort={handleSortingChange}
      loaderRef={loaderRef}
      threshold={THRESHOLD_VALUE}
    />
  );
};

export default withSuspense(UserTable, { fallback: <Loader /> });
